import { useEffect, useState } from "react";
import { agentSlot, type DraftSummary } from "../shared/agent-drafts";
import {
  getPreferences,
  subscribePreferences,
  type AgentStyle,
} from "./preferences";

/*
 * Agents have no roles: every conversation runs the same general agent, and
 * its identity only has to tell concurrent conversations apart. Each style is
 * a set of eight named characters plus a motion language for the four states.
 * The marks are fixed SVG built here from numbers, never from user content.
 */

export type MarkState = "idle" | "working" | "waiting" | "done";

export function markState(status: DraftSummary["status"]): MarkState {
  if (status === "working") return "working";
  if (status === "waiting" || status === "failed") return "waiting";
  if (status === "ready" || status === "applied") return "done";
  return "idle";
}

const svg = (inner: string) =>
  `<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">${inner}</svg>`;
const zz = (ink: string) =>
  `<text x="31" y="10" font-size="8" font-weight="700" fill="${ink}" font-family="sans-serif">z</text><text x="35" y="5" font-size="6" font-weight="700" fill="${ink}" font-family="sans-serif">z</text>`;
const bang = (fill: string) =>
  `<rect x="29" y="0" width="9" height="11" rx="3" fill="${fill}"/><path d="M33.5 2.6v3.6" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/><circle cx="33.5" cy="8.4" r="1" fill="#fff"/>`;
const sparkle = (fill: string) =>
  `<path d="M34 1.5l1.2 3 3 1.2-3 1.2L34 9.9l-1.2-3-3-1.2 3-1.2z" fill="${fill}"/>`;
const extras = (s: MarkState) =>
  s === "idle"
    ? zz("#6b6f80")
    : s === "waiting"
      ? bang("#d6455f")
      : s === "done"
        ? sparkle("#f2a93b")
        : "";

/** A small face whose eyes and mouth follow the state. */
function face(
  cx: number,
  cy: number,
  s: MarkState,
  ink: string,
  k = 1,
  blush = true,
) {
  const dx = 3.4 * k;
  let out = "";
  if (s === "idle")
    out += `<path d="M${cx - dx - 1.6 * k} ${cy}h${3.2 * k}M${cx + dx - 1.6 * k} ${cy}h${3.2 * k}" stroke="${ink}" stroke-width="${1.5 * k}" stroke-linecap="round"/>`;
  else if (s === "done")
    out += `<path d="M${cx - dx - 1.7 * k} ${cy + 0.6 * k}q${1.7 * k} ${-2.6 * k} ${3.4 * k} 0M${cx + dx - 1.7 * k} ${cy + 0.6 * k}q${1.7 * k} ${-2.6 * k} ${3.4 * k} 0" fill="none" stroke="${ink}" stroke-width="${1.5 * k}" stroke-linecap="round"/>`;
  else {
    const r = (s === "waiting" ? 1.9 : 1.5) * k;
    out += `<circle cx="${cx - dx}" cy="${cy}" r="${r}" fill="${ink}"/><circle cx="${cx + dx}" cy="${cy}" r="${r}" fill="${ink}"/>`;
  }
  if (s === "working" || s === "done")
    out += `<path d="M${cx - 1.8 * k} ${cy + 3 * k}q${1.8 * k} ${1.8 * k} ${3.6 * k} 0" fill="none" stroke="${ink}" stroke-width="${1.3 * k}" stroke-linecap="round"/>`;
  if (s === "waiting")
    out += `<circle cx="${cx}" cy="${cy + 3.6 * k}" r="${1.05 * k}" fill="${ink}"/>`;
  if (blush && s !== "idle")
    out += `<circle cx="${cx - dx - 2.2 * k}" cy="${cy + 2.4 * k}" r="${1.4 * k}" fill="#f27a93" opacity=".45"/><circle cx="${cx + dx + 2.2 * k}" cy="${cy + 2.4 * k}" r="${1.4 * k}" fill="#f27a93" opacity=".45"/>`;
  return out;
}

const INK = "#23263a";
const desk: ((s: MarkState) => string)[] = [
  (s) =>
    `<rect x="13" y="3" width="14" height="6" rx="2" fill="#f49ab0"/><rect x="13" y="8" width="14" height="3" fill="#c9ccd8"/><rect x="13" y="11" width="14" height="18" fill="#f6c343"/><path d="M13 29H27L20 38Z" fill="#f2d2a9"/><path d="M18.3 34.5H21.7L20 38Z" fill="${INK}"/>` +
    face(20, 19, s, INK, 0.8),
  (s) =>
    `<rect x="4" y="12" width="32" height="19" rx="5" fill="#f49ab0"/><path d="M4 17a5 5 0 0 1 5-5h6v19H9a5 5 0 0 1-5-5z" fill="#6fa8dc"/>` +
    face(25.5, 20.5, s, INK, 0.85),
  (s) =>
    `<rect x="7" y="9" width="26" height="25" rx="5" fill="#6fa8dc"/><rect x="7" y="9" width="26" height="7" rx="3" fill="#5a93c8"/><circle cx="20" cy="12.5" r="2.4" fill="#2e4d6e"/>` +
    face(20, 23, s, INK, 0.9),
  (s) =>
    `<path d="M7 9a2 2 0 0 1 2-2h22a2 2 0 0 1 2 2v17l-8 8H9a2 2 0 0 1-2-2z" fill="#ffe27a"/><path d="M25 34v-6a2 2 0 0 1 2-2h6z" fill="#f2c94c"/>` +
    face(19, 19, s, INK, 0.95),
  (s) =>
    `<circle cx="20" cy="21" r="15" fill="#9fd3f0"/><circle cx="20" cy="21" r="15" fill="none" stroke="#7dbde3" stroke-width="1.5"/><circle cx="28.5" cy="29" r="4" fill="#f6f7fb"/><path d="M33 16h5v7h-5" fill="#bfe3f7"/>` +
    face(17, 18, s, INK, 0.9),
  (s) =>
    `<rect x="12" y="4" width="16" height="10" rx="3" fill="#f28c38"/><rect x="12" y="12" width="16" height="25" rx="3" fill="#fff6ec" stroke="#f2b07a" stroke-width="1.4"/><rect x="12" y="26" width="16" height="4" fill="#f7c9a0"/>` +
    face(20, 19.5, s, INK, 0.85),
  (s) =>
    `<rect x="15" y="4" width="10" height="7" rx="1.5" fill="${INK}"/><rect x="8" y="10" width="24" height="26" rx="7" fill="#3e4c8a"/><rect x="11.5" y="18" width="17" height="12" rx="3" fill="#e8ecfa"/>` +
    face(20, 23, s, INK, 0.8, false),
  (s) =>
    `<path d="M20 26v11" stroke="#8a8fa3" stroke-width="2.2" stroke-linecap="round"/><ellipse cx="20" cy="27" rx="7" ry="2.4" fill="#c2423a"/><circle cx="20" cy="15" r="11.5" fill="#e5534b"/><circle cx="16" cy="10.5" r="2.4" fill="#f28f87"/>` +
    face(20, 15.5, s, "#3a1210", 0.9, false),
];

const DRAFT_INK = "#2a2740";
const drafting: ((s: MarkState) => string)[] = [
  (s) =>
    `<path d="M16 19L11 36M24 19L29 36" stroke="#7b86a3" stroke-width="4" stroke-linecap="round"/><circle cx="11" cy="36" r="1.6" fill="${DRAFT_INK}"/><rect x="18" y="2" width="4" height="4" rx="1.5" fill="#7b86a3"/><circle cx="20" cy="13.5" r="9.5" fill="#f2a65a"/>` +
    face(20, 13, s, DRAFT_INK, 0.82),
  (s) =>
    `<path d="M8 33.5V10.5c0-1.6 1.9-2.4 3-1.3l22 22c1.1 1.1.3 3-1.3 3H9.5c-.8 0-1.5-.7-1.5-1.5z" fill="#7fc8a9"/><path d="M12.5 29.5v-8l8 8z" fill="#f3f1fa" opacity=".9"/>` +
    face(17.5, 22, s, DRAFT_INK, 0.72),
  (s) =>
    `<rect x="3" y="12" width="34" height="17" rx="5" fill="#f6c343"/><path d="M9 12v4M14 12v6M19 12v4M24 12v6M29 12v4M34 12v4" stroke="#c9952a" stroke-width="1.5" stroke-linecap="round"/>` +
    face(20, 22.5, s, DRAFT_INK, 0.85),
  (s) =>
    `<path d="M4 31a16 16 0 0 1 32 0z" fill="#9ec5f2"/><path d="M11 31a9 9 0 0 1 18 0" fill="#f3f1fa" opacity=".85"/><path d="M8 25l2 1.2M13 19.5l1.6 1.6M20 17v2.2M27 19.5l-1.6 1.6M32 25l-2 1.2" stroke="#5e8cc2" stroke-width="1.3" stroke-linecap="round"/>` +
    face(20, 26, s, DRAFT_INK, 0.7, false),
  (s) =>
    `<path d="M7 28C4 18 12 6 22 7c9 1 14 9 10 15-3 5-9 3-11 8-2 4-11 6-14-2z" fill="#c9a7eb"/><circle cx="25" cy="15" r="2.6" fill="#f3f1fa"/>` +
    face(15, 20, s, DRAFT_INK, 0.8),
  (s) =>
    `<path d="M20 3l11 14-6 19h-10L9 17z" fill="#4e5a7a"/><path d="M20 21v14" stroke="#f3f1fa" stroke-width="1.6" stroke-linecap="round"/><circle cx="20" cy="21" r="1.8" fill="#f3f1fa"/>` +
    face(20, 13, s, "#ffffff", 0.72, false),
  (s) =>
    `<rect x="5" y="8" width="26" height="25" rx="8" fill="#f28c38"/><circle cx="18" cy="20.5" r="4" fill="#e06f1e" opacity=".45"/><path d="M30 26h7v5h-7z" fill="#f6c343"/><path d="M33 26v2M35.5 26v2" stroke="#c9952a" stroke-width="1"/>` +
    face(18, 18.5, s, DRAFT_INK, 0.85),
  (s) =>
    `<rect x="4" y="5" width="32" height="10" rx="4" fill="#6fb3b8"/><rect x="16" y="13" width="8" height="24" rx="3.5" fill="#6fb3b8"/><rect x="16" y="13" width="8" height="3" fill="#5a9da2"/>` +
    face(20, 10, s, DRAFT_INK, 0.62, false),
];

const PEBBLE_COLOURS = [
  "#8fa58a",
  "#c98b6b",
  "#6e7b85",
  "#d2b48a",
  "#6f8f5b",
  "#8e7fa8",
  "#de8a76",
  "#93afbc",
];
const PEBBLE_SHAPES = [
  "M20 4C28 4 33 14 33 23C33 31 27 36 20 36C13 36 7 31 7 23C7 14 12 4 20 4Z",
  "M10 12C14 5 24 4 30 9C36 14 35 22 30 27C26 31 26 36 18 35C9 34 4 26 6 19C7 16 8 14 10 12Z",
  "M20 5C23 5 25 8 28 13L34 25C36 30 33 34 28 34H12C7 34 4 30 6 25L12 13C15 8 17 5 20 5Z",
  "M4 21C4 14 11 10 21 10C30 10 36 14 36 20C36 27 29 31 20 31C11 31 4 28 4 21Z",
];
function pebble(slot: number, s: MarkState) {
  const c = PEBBLE_COLOURS[slot];
  const shape = `<path d="${PEBBLE_SHAPES[slot % 4]}" fill="${c}" transform="rotate(${slot * 47} 20 20)"${s === "idle" ? ' opacity=".45"' : ""}/>`;
  if (s === "waiting")
    return (
      shape +
      `<circle cx="31" cy="8" r="4.5" fill="#c96a4b" stroke="#fff" stroke-width="1.8"/>`
    );
  if (s === "done")
    return shape + `<circle cx="20" cy="20" r="3.4" fill="#fbf8f2"/>`;
  return shape;
}

type StyleDef = {
  label: string;
  names: string[];
  motion: "character" | "ring";
  ring?: (slot: number) => string;
  draw: (slot: number, s: MarkState) => string;
};
const STYLES: Record<AgentStyle, StyleDef> = {
  desk: {
    label: "Desk friends",
    names: ["Milo", "Pip", "Juno", "Remy", "Kiki", "Otto", "Lulu", "Bo"],
    motion: "character",
    draw: (slot, s) => desk[slot](s) + extras(s),
  },
  pebbles: {
    label: "Pebbles",
    names: ["Sol", "Wren", "Rumi", "Ivy", "Kai", "Noor", "Ezra", "Mira"],
    motion: "character",
    draw: pebble,
  },
  "pebbles-ring": {
    label: "Pebbles with rings",
    names: ["Sol", "Wren", "Rumi", "Ivy", "Kai", "Noor", "Ezra", "Mira"],
    motion: "ring",
    ring: (slot) => PEBBLE_COLOURS[slot],
    draw: pebble,
  },
  drafting: {
    label: "Drafting friends",
    names: ["Ziggy", "Momo", "Kit", "Toto", "Bea", "Nico", "Lumi", "Ollie"],
    motion: "character",
    draw: (slot, s) => drafting[slot](s) + extras(s),
  },
};

export function agentName(
  style: AgentStyle,
  d: { id: string; agent?: number | null },
) {
  return STYLES[style].names[agentSlot(d)];
}

export function useAgentStyle(userId: string) {
  const [style, setStyle] = useState(() => getPreferences(userId).agentStyle);
  useEffect(() => {
    setStyle(getPreferences(userId).agentStyle);
    return subscribePreferences(userId, (value) => setStyle(value.agentStyle));
  }, [userId]);
  return style;
}

/** The agent's character, drawn in the user's style, moving with its state. */
export function AgentMark({
  style,
  draft,
  state,
  size,
}: {
  style: AgentStyle;
  draft: { id: string; agent?: number | null };
  state: MarkState;
  size: number;
}) {
  const def = STYLES[style];
  const slot = agentSlot(draft);
  const ring =
    def.motion === "ring"
      ? `<svg class="agent-ring" viewBox="0 0 48 48" aria-hidden="true" focusable="false"><circle class="r1" cx="24" cy="24" r="21.5" stroke="${def.ring!(slot)}"/><circle class="r2" cx="24" cy="24" r="21.5" stroke="${def.ring!(slot)}"/></svg>`
      : "";
  return (
    <span
      className={`agent-mark motion-${def.motion} state-${state}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
      // Fixed artwork from the tables above; no user content reaches this markup.
      dangerouslySetInnerHTML={{
        __html:
          ring +
          `<span class="agent-mark-body">${svg(def.draw(slot, state))}</span>`,
      }}
    />
  );
}

/* Local agents run only on the local server; the setting appears once the runtime reports it is enabled. */
let localAgentsAvailable = false;
export function setLocalAgentsAvailable(value: boolean) {
  localAgentsAvailable = value;
}
export function hasLocalAgents() {
  return localAgentsAvailable;
}
